import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Grid2,
  IconButton,
} from "@mui/material";

import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import CloseButton from "../../../Components/CloseButton";
import VisualizationSettings from "./VisualizationSettings";
import VisualizationPreview from "./VisualizationPreview";
import CustomIcon from "../../../Components/CustomIcon";
import { PrimaryTooltip } from "../../../Components/StyledComponent";
import { resetContinuousChartData } from "../Common/CommonFunctions";
import { fetchEventAnnotations } from "../Common/eventsData";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { calculateIntervalFromTimeWindow, formatChartTimestampForApi } from "../../../Helper/Charts/ChartHelper";

dayjs.extend(utc);
dayjs.extend(timezone);

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 8,
    boxShadow: theme.shadows[5],
    width: "1304px",
    maxWidth: "100%",
    backgroundColor: '#F4F4F4',
    margin: 0,
    maxHeight: '100%',
  },
}));

const EditVisualizationDialog = ({ chartId, entities, batteryID, selectedDate, onSaveChart, events = [], batteryInfo = {}, selectedEntity = null }) => {

  const {t} = useTranslation(['common', 'digital_twin']);
  const [open, setOpen] = useState(false);
  const [previewChartData, setPreviewChartData] = useState({});
  const [chartEntities, setChartEntities] = useState([]);
  const [chartEvents, setChartEvents] = useState([]);
  const [annotationData, setAnnotationData] = useState([]);
  const [isChartLoading, setIsChartLoading] = useState(false);
  const [intervalSeconds, setIntervalSeconds] = useState(300);
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomedRange, setZoomedRange] = useState(null);
  const zoomTimeoutRef = useRef(null);


  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (zoomTimeoutRef.current) {
        clearTimeout(zoomTimeoutRef.current);
      }
    };
  }, []);

  const handleOpen = () => {
    setOpen(true)
  };

  const handleClose = () => {
    setOpen(false);
  };

  const fetchChartData = useCallback(async (entities, startDate, endDate, interval) => {
    if (!entities || entities.length === 0) {
      setPreviewChartData({});
      setIsChartLoading(false);
      return;
    }

    setIsChartLoading(true);

    // Use provided parameters or defaults
    const fetchStartDate = startDate || selectedDate?.value;
    const fetchEndDate = endDate || selectedDate?.value;
    const fetchInterval = interval || 300;

    try {
      const resetChartDataResponse = await resetContinuousChartData(entities, fetchStartDate, fetchEndDate, fetchInterval);
      const {metrics, series} = resetChartDataResponse;

      setPreviewChartData({
        metrics,
        series,
      });
    } catch (error) {
      console.error("Error fetching chart data:", error);
      setPreviewChartData({});
    } finally {
      setIsChartLoading(false);
    }
  }, [selectedDate]);

  const fetchEventsData = useCallback(async (events, startDate) => {
    if (!events || events.length === 0) {
      setAnnotationData([]);
      return;
    }

    setIsChartLoading(true);
    try {
      // Combined BMS-fault + smart-alert annotations for the in-dialog preview.
      const annotations = await fetchEventAnnotations(events, startDate, batteryID);
      setAnnotationData(annotations);
    } catch (error) {
      console.error("Error fetching events data:", error);
      setAnnotationData([]);
    } finally {
      setIsChartLoading(false);
    }
  }, [batteryID]);

  const handleOnEntitiesChange = useCallback((newEntities) => {
    setChartEntities(newEntities);
    // Reset zoom state when entities change
    setIsZoomed(false);
    setZoomedRange(null);
    setIntervalSeconds(300);
    fetchChartData(newEntities, selectedDate?.value, selectedDate?.value, 300);
  }, [fetchChartData, selectedDate]);

  const handleOnEventsChange = useCallback((newEvents) => {
    setChartEvents(newEvents);
    fetchEventsData(newEvents, selectedDate?.value, selectedDate?.value);
  }, [fetchEventsData, selectedDate]);

  const resetChartDataAfterZoom = useCallback((xaxis) => {
    // Clear any existing timeout
    if (zoomTimeoutRef.current) {
      clearTimeout(zoomTimeoutRef.current);
    }

    // Debounce the zoom update to prevent multiple rapid calls
    zoomTimeoutRef.current = setTimeout(async () => {
      // Use the xaxis parameter from beforeZoom event
      const minX = xaxis?.min;
      const maxX = xaxis?.max;

      if (!minX || !maxX) {
        setIsChartLoading(false);
        return;
      }

      // Calculate the appropriate interval based on the visible time window
      const calculatedInterval = calculateIntervalFromTimeWindow(minX, maxX);

      // Convert minX and maxX timestamps to date strings for the API
      const zoomedStartDate = formatChartTimestampForApi(minX);
      const zoomedEndDate = formatChartTimestampForApi(maxX);

      // Store zoom state
      setIsZoomed(true);
      setZoomedRange({ minX, maxX });
      setIntervalSeconds(calculatedInterval);

      // Fetch new data with the zoomed range
      await fetchChartData(chartEntities, zoomedStartDate, zoomedEndDate, calculatedInterval);
    }, 500); // 500ms debounce
  }, [chartEntities, fetchChartData]);

  const handleZoomReset = useCallback(async () => {
    // Reset interval to default (300 seconds / 5 minutes)
    const defaultInterval = 300;
    setIntervalSeconds(defaultInterval);

    // Reset zoom state
    setIsZoomed(false);
    setZoomedRange(null);

    // Refetch chart data with original date range and default interval
    await fetchChartData(chartEntities);
  }, [chartEntities, fetchChartData]);

  const handleOnSave = () => {
    onSaveChart(chartEntities, chartEvents);
    handleClose();
  }

  useEffect(() => {
    if (open && entities) {
      setChartEntities(entities);
      setChartEvents(events);
      fetchChartData(entities);
      if (events && events.length > 0) {
        fetchEventsData(events, selectedDate?.value, selectedDate?.value);
      }
    }
  }, [open, entities, events, fetchChartData, fetchEventsData, selectedDate])

  return (
    <>
      <PrimaryTooltip title={"Edit Visualization"} placement="bottom">
        <Box>
          <IconButton sx={{
            px: 1.5,
            py: 1,
            borderRadius: '17px',
            '&:hover': {backgroundColor: '#F7F7FF'},
            '&:hover svg': { color: '#6D72F6' }
          }} onClick={handleOpen}>
            <CustomIcon name="edit-icon" height="16px" width="16px" />
          </IconButton>
        </Box>
      </PrimaryTooltip>

      <StyledDialog open={open} onClose={handleClose}>
        <DialogContent sx={{py: 3}}>
          <Grid2 sx={{ mb: 3 }}>
            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3}}>
              <Typography variant="fw500s20c000000" sx={{ lineHeight: '24px' }}>Edit Visualisation</Typography>
              <CloseButton onClick={handleClose} />
            </Box>
            <Grid2 container gap={1.5} flexWrap={'nowrap'}>
                <VisualizationSettings batteryID={batteryID} entities={chartEntities} events={chartEvents} onEntitiesChange={handleOnEntitiesChange} onEventsChange={handleOnEventsChange} />
                <VisualizationPreview
                  chartId={chartId}
                  selectedDate={selectedDate}
                  chartData={previewChartData}
                  onSave={handleOnSave}
                  isEdit={true}
                  isLoading={isChartLoading}
                  onZoom={resetChartDataAfterZoom}
                  onZoomReset={handleZoomReset}
                  zoomedRange={zoomedRange}
                  setLoading={setIsChartLoading}
                  isZoomed={isZoomed}
                  annotationData={annotationData}
                  batteryInfo={batteryInfo}
                  selectedEntity={selectedEntity}
                />
            </Grid2>
          </Grid2>
        </DialogContent>
      </StyledDialog>
    </>
  );
};

export default EditVisualizationDialog;
